import type {
  RouteRegion,
  TemperatureType,
  TripLoadType,
  TripType,
  VehicleTypes,
} from '@hero/schema';
import type { RegionCity } from '@hero/api';

/** Pricing scenario — drives whether the org is the seller or the buyer. */
export type SheetSide = 'sell' | 'buy';

/** Per-row dirty state — used to gate the "Save all" button and render badges. */
export type SheetRowStatus = 'unchanged' | 'modified' | 'new' | 'deleted';

/**
 * Single editable row inside the price sheet. Each row is one Route candidate;
 * `routeId` is `null` for newly-added rows that haven't been persisted yet.
 */
export interface SheetRow {
  /** Local id (UUID) used as React key + selection key. Stable across edits. */
  id: string;
  /** Backend Route `_id` when this row maps to an existing record. */
  routeId: string | null;
  origin: RouteRegion | null;
  destination: RouteRegion | null;
  vehicles: VehicleTypes[];
  tripType: TripType;
  loadType: TripLoadType;
  temperatureType: TemperatureType[];
  price: number;
  /** Tracks whether the row is dirty so we can gate "Save all". */
  status: SheetRowStatus;
}

// =============================================================================
// SheetScope — who is this price list for?
// =============================================================================
//
// The pricing sheet supports three mutually-exclusive scopes. Every server
// round-trip (list, create, update) is parameterised by the active scope, and
// the entire UX (header, save semantics, list filtering, error messages)
// branches off the `kind` discriminator below.
//
// Why a discriminated union and not three independent fields:
//   - Each branch carries exactly the fields the next step (list / create)
//     needs — no "is `counterparty` set?", "is `agreementId` set?", "are they
//     consistent?" guard mess at the call sites.
//   - TypeScript narrows inside `switch (scope.kind)`, so adding a new branch
//     later (e.g. `{ kind: 'rate_card' }` for portable templates) becomes a
//     compile-time exhaustiveness error in every consumer that hasn't been
//     updated. That's the safety net we want for a money-relevant flow.
//
// Mapping to the Route domain model (`apps/backend/src/app/route/route.service.ts`):
//
//   | scope.kind  | route.seller             | route.buyer              | route.agreementId | isOwnRoute |
//   | ----------- | ------------------------ | ------------------------ | ----------------- | ---------- |
//   | 'own'       | me (when side='sell')    | me (when side='buy')     | —                 | true       |
//   | 'direct'    | me                       | counterparty             | —                 | false      |
//   | 'agreement' | me                       | derived from parties[]   | agreement.id      | false      |
//   |             | (sides swap when side='buy' — see `getDirectedParties`)                              |
//
// Why `'own'` exists at all:
//   An "own" price list is the catalog you publish before you have a specific
//   buyer in mind — the resolver falls back to it when there's no directed
//   route or agreement between the two parties on the lane. Many carriers
//   want this as their default workspace.
//
// Why `'direct'` is separate from `'agreement'`:
//   A direct route is an ad-hoc bilateral price (no formal contract envelope)
//   between two real Hero orgs. An agreement is the formal envelope — same
//   two parties, but the backend additionally enforces `Agreement.parties[]`
//   match and cascades archive when the agreement is terminated.

/** A counterparty party in the `'direct'` and `'agreement'` scopes. */
export interface SheetScopeParty {
  /**
   * MongoDB id of the counterparty.
   *
   * Resolved server-side by `RouteDTOService.getPartyRelation`, which
   * probes the unified `Org` collection first (Hero accounts) and falls
   * back to the `Client` collection (per-Org CRM records). Both are
   * first-class parties:
   *
   * - Hit on `Org` → Party with `target: 'org'`,
   *   `ids: [{org_id}]`. Used when the counterparty is a signed-up Hero
   *   account.
   * - Hit on `Client` → Party with `target: 'client'`,
   *   `ids: [{client_id}, {profile_id?}, {org_id?}]`. The optional
   *   bridged ids come from `Client.linkedParty` (and
   *   `Profile.linkedOrgId` when present). Used for CRM-only entries
   *   that haven't (yet) been linked to a real Org.
   *
   * Per-Client pricing surfaces (e.g. `ClientPricingTab`) pass the
   * Client `_id` directly; the bridge ids are derived server-side, the
   * frontend stays dumb.
   */
  id: string;
  name: string;
}

/**
 * Open ("catalog") price list. No counterparty, no agreement — the row is
 * created with `isOwnRoute=true` and only the active side (`sell` → seller,
 * `buy` → buyer) is the current org. This is the default scope on first
 * mount.
 */
export interface SheetScopeOwn {
  kind: 'own';
}

/**
 * Ad-hoc bilateral price between the current org and one specific
 * counterparty, with no agreement envelope. Used when a customer asks "how
 * much do you charge for X?" and you want to pin the answer without going
 * through the agreement workflow.
 *
 * The counterparty id may be either a Hero `Org` `_id` (signed-up
 * account) or a CRM `Client` `_id` (pure address-book entry); the
 * backend probes both — see `SheetScopeParty` docstring above. The
 * per-Client Pricing tab seeds with the Client `_id`; the sheet header
 * (`PricingSheetHeader`) mints values via the unified entity-directory
 * picker (Profile + Org). We surface a clear toast when
 * that happens so the failure mode is legible.
 */
export interface SheetScopeDirect {
  kind: 'direct';
  counterparty: SheetScopeParty;
}

/**
 * Price list bound to an existing agreement. The counterparty is *derived*
 * from `Agreement.parties[]` (the side that isn't the current org) — we
 * never ask the user to pick it twice. Every row saved in this scope
 * carries the `agreementId` on the create payload, which:
 *
 *   1. forces the parties-match validation on the backend
 *      (`route.service.ts > validate`), so a typo can't slip a route into
 *      the wrong agreement;
 *   2. lets `RouteService.archiveByAgreement` cascade-tombstone the routes
 *      when the agreement is terminated.
 */
export interface SheetScopeAgreement {
  kind: 'agreement';
  agreement: {
    id: string;
    /** Display label (e.g. agreement type or counterparty name). */
    label?: string;
  };
  counterparty: SheetScopeParty;
}

/**
 * Sheet-wide scope union. Always set; the initial value is `{ kind: 'own' }`.
 *
 * Switching scope clears the row selection eagerly (so the bulk bar doesn't
 * blink with stale ids) and re-fires `useRouteListApi` with a new
 * `listParams` — the server result is then seeded into the store.
 */
export type SheetScope = SheetScopeOwn | SheetScopeDirect | SheetScopeAgreement;

/** Default starting scope — open catalog, no counterparty. */
export const INITIAL_SHEET_SCOPE: SheetScope = { kind: 'own' };

/**
 * Return the counterparty party when the scope has one, else `undefined`.
 * Useful when the consumer doesn't care whether it's direct or agreement,
 * only "is there an other side, and what is it?".
 */
export function getScopeCounterparty(
  scope: SheetScope,
): SheetScopeParty | undefined {
  switch (scope.kind) {
    case 'own':
      return undefined;
    case 'direct':
    case 'agreement':
      return scope.counterparty;
  }
}

/** Return the agreement id when the scope is `'agreement'`, else `undefined`. */
export function getScopeAgreementId(scope: SheetScope): string | undefined {
  return scope.kind === 'agreement' ? scope.agreement.id : undefined;
}

/**
 * Filters narrow which rows are visible in the sheet without touching the
 * underlying data. New (unsaved) rows always bypass the filter so adding a row
 * never makes it visually disappear.
 *
 * We keep the full {@link RegionCity} objects (not just ids) so the empty
 * state can prefill a freshly-added row from the filter without doing an
 * extra lookup.
 */
export interface SheetFilters {
  /** Origin region. `null` = unconstrained. */
  origin: RegionCity | null;
  /** Destination region. `null` = unconstrained. */
  destination: RegionCity | null;
  /** Empty array = unconstrained. Matches when row.vehicles intersects. */
  vehicles: VehicleTypes[];
}

export const EMPTY_SHEET_FILTERS: SheetFilters = {
  origin: null,
  destination: null,
  vehicles: [],
};

export function isFilterActive(filters: SheetFilters): boolean {
  return (
    filters.origin !== null ||
    filters.destination !== null ||
    filters.vehicles.length > 0
  );
}
